import type { ProductSecondaryIdentifier } from '@xbcb/api-gateway-client';
import { pick } from 'lodash';
import { transformLines } from './workOrder/usConsumptionEntry';

/**
 * The Product page reuses the shared "entry" line components, so the tariff
 * engine (and Section 232 automation / reasonability / HTS classification) runs
 * here exactly as it does on a US consumption entry. The Product input is a much narrower
 * type, so we map the engine's output down to it on save instead of suppressing
 * the engine.
 */
const PRODUCT_US_ENTRY_LINE_FIELDS: string[] = [
  'adCase',
  'adCaseNumber',
  'adDisclaimerCode',
  'adPaymentMethod',
  'adValue',
  'aluminumExclusionNumber',
  'aluminumRequirements',
  'cvCase',
  'cvCaseNumber',
  'cvDisclaimerCode',
  'cvPaymentMethod',
  'cvValue',
  'description',
  'doesNotContainAluminum',
  'doesNotContainSteel',
  'feeOptions',
  'licenseCode',
  'licenseNumber',
  'manufacturer',
  'origin',
  'poNumber',
  'productClaimCode',
  'ruling',
  'spi',
  'steelExclusionNumber',
  'steelRequirements',
  'tariffs',
  'taxOptions',
];

const PRODUCT_US_TARIFF_FIELDS: string[] = [
  'extraPgaFlags',
  'hts',
  'htsNumber',
  'pga',
  'pgaDescription',
  'unitAssist',
  'unitAssistPercentage',
  'unitReportingQuantities',
  'unitValue',
];

/* Narrow one (already schema-transformed) line to the Product input shape. */
const toProductEntryLine = (line: any): any => {
  const pruned: any = pick(line, PRODUCT_US_ENTRY_LINE_FIELDS);
  if (Array.isArray(line?.tariffs)) {
    pruned.tariffs = line.tariffs.map((tariff: any) =>
      pick(tariff, PRODUCT_US_TARIFF_FIELDS),
    );
  }
  return pruned;
};

/*
 * Expand each entry line into the engine's evaluated `lines` (merging so any
 * source-only field the engine line omits is preserved), returning the flat
 * list of lines destined for `complianceDetails.us.entryLines`. Engine-line
 * fields win on conflict. A line the engine never touched passes through
 * unchanged. The still-present `lines`/evaluator fields are dropped later by
 * `toProductEntryLine`.
 */
const expandEngineLines = (entryLines: any[]): any[] =>
  entryLines.flatMap((entryLine) => {
    const { lines: engineLines, ...source } = entryLine ?? {};
    if (!Array.isArray(engineLines) || engineLines.length === 0) {
      return [entryLine];
    }
    return engineLines.map((engineLine: any) => ({ ...source, ...engineLine }));
  });

/*
 * Map the engine's entry-line output to the Product input schema: expand split
 * lines, run the shared line transform (PGA/CPSC/etc., same as the entry save),
 * then narrow every line and tariff to the Product fields.
 */
const mapEntryLinesToProductSchema = (us: any): void => {
  if (!Array.isArray(us?.entryLines)) {
    transformLines.toSchema({ lines: us?.entryLines });
    return;
  }
  us.entryLines = expandEngineLines(us.entryLines);
  transformLines.toSchema({ lines: us.entryLines });
  us.entryLines = us.entryLines.map(toProductEntryLine);
};

export const transformProduct = {
  toForm: ({ existingRecord: newRecord }: { existingRecord: any }) => {
    transformLines.toForm({
      lines: newRecord.complianceDetails?.us?.entryLines,
    });
    return newRecord;
  },

  toCreateSchema: ({ input }: { input: any }) => {
    input.secondaryIdentifiers = input.secondaryIdentifiers?.filter(
      (si: ProductSecondaryIdentifier) => si.value,
    );
    mapEntryLinesToProductSchema(input.complianceDetails?.us);
    return input;
  },

  toUpdateSchema: ({ input }: { input: any }) => {
    input.secondaryIdentifiers = input.secondaryIdentifiers?.filter(
      (si: ProductSecondaryIdentifier) => si.value,
    );
    mapEntryLinesToProductSchema(input.complianceDetails?.us);
    delete input.shipper;
    return input;
  },
};
